// ManagerReportsPage — the manager's cross-domain team hub (redesign): leave,
// work assignments, work items and learning in one place, all from REAL
// team-scoped APIs (server-side narrowing; nothing mock). Domain tabs appear
// only when the tenant holds the product; every fetch fails soft so one
// missing module never blanks the page.
//
// SCOPE RULE (deliberate): every tab shows ONLY the manager's DIRECT reports.
// Some feeds (attendance days, tenant-wide lists an HR-role manager can read)
// return more than the team — the page filters everything to the direct-team
// member set so a manager never sees another team's numbers here.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import {
  Users, CalendarOff, CalendarCheck, Clock, Loader2, BarChart3, Briefcase, ClipboardList, GraduationCap, Award,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  getMySubordinates, getOrgBalances, getTeamOut, getPendingApprovals,
  type Employee, type OrgEmployeeBalances,
} from '@/lib/hrApi';
import { listAssignments as listTeamWorkAssignments, pendingApprovals as pendingAssignmentApprovals, type WorkAssignment } from '@/services/workAssignmentService';
import { listWorkItems, type WorkItem } from '@/services/workService';

//: The server clamps to 200. Asking for its default (50) is what made a
//: team of eight show one work item.
const WORK_PAGE = 200;
import { bucketByStatus, rowsForBucket, statusLabel } from './statusBuckets';
import { listAssignments as listLearningAssignments, listCertifications, type Assignment as LearningAssignment, type Certification } from '@/services/learningService';
import { listDays as listAttendanceDays, formatMinutes, type AttendanceDay } from '@/services/attendanceService';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { formatDate } from '@/lib/orgFormat';

const firstName = (e: Employee) => (e.first_name || e.email || '?').split(' ')[0];
const fullName = (e?: Employee) => (e ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email : '—');

const ORANGE = '#F28A3A';
const ORANGE_SOFT = '#F6B26B';

const StatTile: React.FC<{ label: string; value: React.ReactNode; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <Card className="border-orange-100 dark:border-orange-500/20">
    <div className="p-5 flex items-center gap-4">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-foreground leading-tight">{value}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </div>
    </div>
  </Card>
);

const ChartCard: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode; className?: string; action?: React.ReactNode }> = ({ title, icon, children, className, action }) => (
  <Card className={`border-orange-100 dark:border-orange-500/20 ${className || ''}`}>
    <CardHeader className="rounded-t-xl">
      <CardTitle className="flex items-center gap-2 text-orange-700">
        {icon}{title}
        {action && <span className="ml-auto font-normal">{action}</span>}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-6">{children}</CardContent>
  </Card>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div className="py-12 text-center text-muted-foreground text-sm">{text}</div>
);

const chipTone: Record<string, string> = {
  scheduled: 'bg-info-soft text-info-strong', in_progress: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  completed: 'bg-success-soft text-success-strong', cancelled: 'bg-muted text-muted-foreground', draft: 'bg-muted text-muted-foreground',
  assigned: 'bg-muted text-muted-foreground', verified: 'bg-success-soft text-success-strong',
  submitted: 'bg-info-soft text-info-strong', approved: 'bg-success-soft text-success-strong', returned: 'bg-warning-soft text-warning-strong',
};
const StatusChip: React.FC<{ v: string }> = ({ v }) => (
  <Badge className={`border-0 text-[10px] capitalize ${chipTone[v] || 'bg-muted text-muted-foreground'}`}>{v.replace(/_/g, ' ')}</Badge>
);

export const ManagerReportsPage: React.FC = () => {
  const { hasProduct } = useEntitlements();
  const showWork = hasProduct('work');
  // Tenant-aware link to one work item. The app is mounted under
  // /t/<slug>/app/..., so an absolute '/app/work/x' would drop the tenant and
  // land on the workspace picker. Same trick Team Management uses: keep
  // everything up to the current page's own segment.
  const location = useLocation();
  const workPath = (id: string) =>
    `${location.pathname.replace(/\/[^/]+\/?$/, '')}/work/${id}`;
  const showLearning = hasProduct('learning');

  const [team, setTeam] = useState<Employee[]>([]);
  const [balances, setBalances] = useState<Record<string, OrgEmployeeBalances>>({});
  const [outNow, setOutNow] = useState(0);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [assignments, setAssignments] = useState<WorkAssignment[]>([]);
  const [pendingAsg, setPendingAsg] = useState(0);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [workTruncated, setWorkTruncated] = useState(false);
  // Which bar the manager clicked, if any. A chart that answers "22 completed"
  // and then cannot tell you WHICH 22 is a dead end — the items are already
  // loaded, so the only thing missing was somewhere to put them.
  const [workStatusPick, setWorkStatusPick] = useState<string | null>(null);
  const [asgStatusPick, setAsgStatusPick] = useState<string | null>(null);
  const [learning, setLearning] = useState<LearningAssignment[]>([]);
  const [certs, setCerts] = useState<Certification[]>([]);
  const [attDays, setAttDays] = useState<AttendanceDay[]>([]);
  const [days, setDays] = useState(30); // period selector: 30/60/90
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const fromDate = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
        // ⚠️ THE SERVER SAYS WHO THE TEAM IS. This downloaded the whole
        // company's directory and kept `manager_id === me` in the browser —
        // every manager's browser received every employee record to show
        // eight of them.
        const reports = await getMySubordinates(false).catch(() => [] as Employee[]);
        const userIds = reports.map((e) => e.user_id).filter(Boolean) as string[];
        const [bal, out, pend, asg, pAsg, work, learn, cert, att] = await Promise.all([
          userIds.length ? getOrgBalances(userIds).catch(() => [] as OrgEmployeeBalances[]) : Promise.resolve([] as OrgEmployeeBalances[]),
          getTeamOut(1, false, 'direct').catch(() => [] as any[]),
          getPendingApprovals().catch(() => [] as any[]),
          listTeamWorkAssignments({ from: fromDate }).catch(() => [] as WorkAssignment[]),
          pendingAssignmentApprovals().catch(() => [] as WorkAssignment[]),
          // ⚠️ ASK FOR THE TEAM'S WORK, NOT THE FIRST PAGE OF EVERYTHING.
          // This was listWorkItems({}) — no scope, no limit — so the server
          // returned its DEFAULT 50 items from everything the manager can
          // read across the org, and the client then filtered those 50 down
          // to the direct reports. At Fern most of the first 50 belong to
          // other people, so a team of 8 showed one work item. The server
          // already resolves `scope=team` against the caller's population;
          // the type even says "the client never filters".
          showWork
            ? listWorkItems({ scope: 'team', limit: WORK_PAGE })
                .then((p: any) => ({ items: p?.items || p || [], total: p?.total }))
                .catch(() => ({ items: [] as WorkItem[], total: 0 }))
            : Promise.resolve({ items: [] as WorkItem[], total: 0 }),
          showLearning ? listLearningAssignments(true).catch(() => [] as LearningAssignment[]) : Promise.resolve([] as LearningAssignment[]),
          showLearning ? listCertifications(true).catch(() => [] as Certification[]) : Promise.resolve([] as Certification[]),
          listAttendanceDays({ from: fromDate }).catch(() => [] as AttendanceDay[]),
        ]);
        if (!active) return;
        setTeam(reports);
        setBalances(Object.fromEntries(bal.map((b) => [String(b.employee_id), b])));
        setOutNow(Array.isArray(out) ? out.length : 0);
        setPendingAsg(Array.isArray(pAsg) ? pAsg.length : 0);
        // Direct-team scoping: filter every feed to the manager's own reports.
        const teamEmpIds = new Set(reports.map((r) => r.id));
        const teamUserIds = new Set(reports.map((r) => String(r.user_id)).filter((x) => x !== 'undefined' && x !== 'null'));
        // The approver queue is tenant-wide for an owner; this page is about
        // THIS team, so count only the team's requests.
        setPendingLeave(Array.isArray(pend)
          ? pend.filter((r: any) => teamUserIds.has(String(r.employee_id))).length : 0);
        setAssignments(asg.filter((a) => teamEmpIds.has(a.employeeId)));
        // Narrow the server's population (which can be the whole reporting
        // subtree) to THIS manager's immediate reports, matching the page's
        // own promise. An item with no owner is nobody's direct-report work
        // and used to pass this filter unconditionally.
        const workRows: WorkItem[] = Array.isArray(work?.items) ? work.items : [];
        setWorkItems(workRows.filter((w: WorkItem) =>
          !!w.owner_employee_id && teamEmpIds.has(w.owner_employee_id)));
        // ⚠️ Say so when the page is not the whole story — but only when it is
        // TRUE OF WHAT IS ON SCREEN. This compared the server's `total` with
        // the rows it returned, and then the list was narrowed again below. For
        // a tenant-wide caller the server's total was the whole company, so the
        // page showed two items under "showing the first 200 for your team —
        // there are more", which is wrong twice over. A full page back is the
        // only truncation signal that survives the narrowing.
        setWorkTruncated(workRows.length >= WORK_PAGE);
        setLearning(learn.filter((a) => teamUserIds.has(String(a.user_id))));
        setCerts(cert.filter((c) => teamUserIds.has(String(c.user_id))));
        setAttDays(att.filter((d) => teamEmpIds.has(d.employee_id)));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWork, showLearning, days]);

  const byEmpId = useMemo(() => Object.fromEntries(team.map((e) => [e.id, e])), [team]);
  const byUserId = useMemo(() => Object.fromEntries(team.map((e) => [String(e.user_id), e])), [team]);

  // ---- Leave aggregates (unchanged logic) ----
  const totalTaken = useMemo(() => Object.values(balances).reduce((s, b) => s + (b.total_taken || 0), 0), [balances]);
  const leaveChart = useMemo(
    () => team.map((e) => {
      const b = e.user_id ? balances[String(e.user_id)] : undefined;
      return { name: firstName(e), taken: Number(b?.total_taken ?? 0), remaining: Number(b?.total_available ?? 0) };
    }).filter((d) => d.taken || d.remaining),
    [team, balances],
  );
  const byType = useMemo(() => {
    const m = new Map<string, number>();
    Object.values(balances).forEach((b) =>
      (b.balances || []).forEach((x) => m.set(x.type || x.type_code, (m.get(x.type || x.type_code) || 0) + (x.taken || 0))));
    return Array.from(m.entries()).map(([type, taken]) => ({ type, taken })).filter((d) => d.taken > 0).sort((a, b) => b.taken - a.taken);
  }, [balances]);
  const maxType = Math.max(1, ...byType.map((d) => d.taken));
  const fmtDays = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : String(Math.round(Number(n) * 10) / 10));

  // ---- Assignment aggregates (last 30 days) ----
  // Fixed order so the bars do not reshuffle as work moves between states.
  const asgByStatus = useMemo(
    () => bucketByStatus(assignments, ['scheduled', 'in_progress', 'completed', 'cancelled']),
    [assignments]);
  const asgByType = useMemo(() => {
    const m = new Map<string, number>();
    assignments.forEach((a) => m.set(a.typeLabel || a.typeKey, (m.get(a.typeLabel || a.typeKey) || 0) + 1));
    return Array.from(m.entries()).map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [assignments]);
  const maxAsgType = Math.max(1, ...asgByType.map((d) => d.count));
  const activeAsg = assignments.filter((a) => a.status === 'scheduled' || a.status === 'in_progress');

  // ---- Work-item aggregates ----
  const openWork = workItems.filter((w) => !['completed', 'cancelled'].includes(w.status));
  // No fixed order here: work statuses are tenant-configurable, so the chart
  // shows whatever exists, busiest first.
  const workByStatus = useMemo(() => bucketByStatus(workItems), [workItems]);
  const overdueWork = openWork.filter((w) => w.planned_end_at && new Date(w.planned_end_at) < new Date());

  // ---- Learning aggregates (team-narrowed server-side) ----
  const learnOpen = learning.filter((a) => a.status === 'assigned' || a.status === 'in_progress');
  const learnDone = learning.filter((a) => a.status === 'completed' || a.status === 'verified');
  const learnOverdue = learnOpen.filter((a) => a.due_date && new Date(a.due_date) < new Date());
  const completionPct = learning.length ? Math.round((learnDone.length / learning.length) * 100) : null;
  const expiringCerts = certs.filter((c) => c.expires_on && new Date(c.expires_on) < new Date(Date.now() + 60 * 86400000));

  // ---- Attendance aggregates (per direct report, selected period) ----
  const attByMember = useMemo(() => {
    const m = new Map<string, { days: number; net: number; late: number; ot: number }>();
    attDays.forEach((d) => {
      const cur = m.get(d.employee_id) || { days: 0, net: 0, late: 0, ot: 0 };
      cur.days += 1; cur.net += d.net_minutes || 0; cur.late += d.late_minutes || 0; cur.ot += d.potential_ot_minutes || 0;
      m.set(d.employee_id, cur);
    });
    return team.map((e) => ({ emp: e, ...(m.get(e.id) || { days: 0, net: 0, late: 0, ot: 0 }) }));
  }, [attDays, team]);
  const attTotals = useMemo(
    () => attByMember.reduce((s2, r) => ({ days: s2.days + r.days, net: s2.net + r.net, late: s2.late + r.late, ot: s2.ot + r.ot }), { days: 0, net: 0, late: 0, ot: 0 }),
    [attByMember],
  );
  const attChart = useMemo(
    () => attByMember.filter((r) => r.days > 0).map((r) => ({ name: firstName(r.emp), hours: Math.round((r.net / 60) * 10) / 10 })),
    [attByMember],
  );

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const tiles = [
    { label: 'Team size', value: team.length, icon: <Users className="w-5 h-5" /> },
    { label: 'Out today', value: outNow, icon: <CalendarOff className="w-5 h-5" /> },
    { label: 'Pending approvals', value: pendingLeave + pendingAsg, icon: <Clock className="w-5 h-5" /> },
    { label: 'Active assignments', value: activeAsg.length, icon: <Briefcase className="w-5 h-5" /> },
    ...(showWork ? [{ label: 'Open work items', value: openWork.length, icon: <ClipboardList className="w-5 h-5" /> }] : []),
    ...(showLearning ? [{ label: 'Training overdue', value: learnOverdue.length, icon: <GraduationCap className="w-5 h-5" /> }] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h2 text-charcoal">Team Reports</h1>
          <p className="text-muted-foreground mt-1">Your direct team only — leave, assignments, attendance{showWork ? ', work' : ''}{showLearning ? ', learning' : ''} — in one place.</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Period</span>
          <select
            className="h-9 px-2 rounded-md border border-border bg-card"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      <div className={`grid grid-cols-2 lg:grid-cols-${Math.min(tiles.length, 6)} gap-4`}>
        {tiles.map((s) => <StatTile key={s.label} {...s} />)}
      </div>

      <Tabs defaultValue="leave" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          {showWork && <TabsTrigger value="work">Work</TabsTrigger>}
          {showLearning && <TabsTrigger value="learning">Learning</TabsTrigger>}
        </TabsList>

        {/* ---- Leave ---- */}
        <TabsContent value="leave" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard className="lg:col-span-2" title="Leave used vs remaining" icon={<BarChart3 className="w-5 h-5" />}>
              {leaveChart.length === 0 ? <Empty text="No leave-balance data for your team yet." /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={leaveChart} barGap={0} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,128,152,0.18)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid rgba(120,128,152,0.2)', fontSize: 12 }} formatter={(v: any) => [`${fmtDays(Number(v))}d`]} />
                    <Bar dataKey="taken" name="Taken" fill={ORANGE} radius={[6, 6, 0, 0]} />
                    <Bar dataKey="remaining" name="Remaining" fill={ORANGE_SOFT} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard title="Days taken by type">
              {byType.length === 0 ? <Empty text="No leave taken yet." /> : (
                <div className="space-y-3">
                  {byType.map((d) => (
                    <div key={d.type}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-foreground font-medium capitalize">{d.type}</span>
                        <span className="text-muted-foreground">{fmtDays(d.taken)}d</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(d.taken / maxType) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ChartCard>
          </div>
          <ChartCard title="Team leave balances" icon={<Users className="w-5 h-5" />}>
            {team.length === 0 ? <Empty text="No team members report to you yet." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4 font-medium">Member</th>
                      <th className="py-2 pr-4 font-medium">Entitled</th>
                      <th className="py-2 pr-4 font-medium">Taken</th>
                      <th className="py-2 pr-4 font-medium">Pending</th>
                      <th className="py-2 font-medium">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.map((e) => {
                      const b = e.user_id ? balances[String(e.user_id)] : undefined;
                      return (
                        <tr key={e.id} className="border-b border-border">
                          <td className="py-2 pr-4 text-foreground">{fullName(e)}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{fmtDays(b?.total_entitled)}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{fmtDays(b?.total_taken)}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{fmtDays(b?.total_pending)}</td>
                          <td className="py-2 text-foreground font-medium">{fmtDays(b?.total_available)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </TabsContent>

        {/* ---- Work Assignments (client visits, field work, travel) ---- */}
        <TabsContent value="assignments" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard className="lg:col-span-2" title={`Assignments by status (last ${days} days)`} icon={<Briefcase className="w-5 h-5" />}>
              {assignments.length === 0 ? <Empty text="No work assignments in the last 30 days." /> : (
                <>
                <p className="text-[11px] text-muted-foreground mb-1">Select a bar to list those assignments.</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={asgByStatus} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,128,152,0.18)" vertical={false} />
                    <XAxis dataKey="status" tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid rgba(120,128,152,0.2)', fontSize: 12 }} />
                    <Bar dataKey="count" name="Assignments" fill={ORANGE} radius={[6, 6, 0, 0]}
                      className="cursor-pointer"
                      onClick={(d: any) => setAsgStatusPick((prev) =>
                        prev === d?.key ? null : (d?.key ?? null))} />
                  </BarChart>
                </ResponsiveContainer>
                </>
              )}
            </ChartCard>
            <ChartCard title="By type">
              {asgByType.length === 0 ? <Empty text="Nothing logged yet." /> : (
                <div className="space-y-3">
                  {asgByType.map((d) => (
                    <div key={d.type}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-foreground font-medium">{d.type}</span>
                        <span className="text-muted-foreground">{d.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(d.count / maxAsgType) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                  {pendingAsg > 0 && (
                    <p className="text-xs text-muted-foreground pt-2">{pendingAsg} assignment(s) awaiting your approval — see Work Assignments.</p>
                  )}
                </div>
              )}
            </ChartCard>
          </div>
          {/* Drill-down for the selected status bar. "Completed: 6" with no way
              to see WHICH 6 is the same dead end the Work tab had. */}
          {asgStatusPick && (() => {
            const picked = rowsForBucket(assignments, asgStatusPick);
            return (
              <ChartCard
                title={`${statusLabel(asgStatusPick)} · ${picked.length} assignment${picked.length === 1 ? '' : 's'}`}
                icon={<Briefcase className="w-5 h-5" />}
                action={(
                  <button type="button" onClick={() => setAsgStatusPick(null)}
                    className="text-xs text-muted-foreground hover:text-foreground underline">
                    Clear
                  </button>
                )}
              >
                {picked.length === 0 ? <Empty text="No assignments with that status." /> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b border-border">
                          <th className="py-2 pr-4 font-medium">Member</th>
                          <th className="py-2 pr-4 font-medium">Assignment</th>
                          <th className="py-2 pr-4 font-medium">Type</th>
                          <th className="py-2 pr-4 font-medium">Planned</th>
                          <th className="py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {picked.slice(0, 50).map((a) => (
                          <tr key={a.id} className="border-b border-border">
                            <td className="py-2 pr-4 text-foreground">{fullName(byEmpId[a.employeeId])}</td>
                            <td className="py-2 pr-4 text-foreground">{a.title}</td>
                            <td className="py-2 pr-4 text-muted-foreground">{a.typeLabel || a.typeKey}</td>
                            <td className="py-2 pr-4 text-muted-foreground">{a.plannedStartAt ? formatDate(a.plannedStartAt) : '—'}</td>
                            <td className="py-2"><StatusChip v={a.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {picked.length > 50 && (
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Showing the first 50 of {picked.length}.
                      </p>
                    )}
                  </div>
                )}
              </ChartCard>
            );
          })()}

          <ChartCard title="Active & upcoming" icon={<Briefcase className="w-5 h-5" />}>
            {activeAsg.length === 0 ? <Empty text="Nothing active or scheduled right now." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4 font-medium">Member</th>
                      <th className="py-2 pr-4 font-medium">Assignment</th>
                      <th className="py-2 pr-4 font-medium">Type</th>
                      <th className="py-2 pr-4 font-medium">Planned</th>
                      <th className="py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeAsg.slice(0, 15).map((a) => (
                      <tr key={a.id} className="border-b border-border">
                        <td className="py-2 pr-4 text-foreground">{fullName(byEmpId[a.employeeId])}</td>
                        <td className="py-2 pr-4 text-foreground">{a.title}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{a.typeLabel || a.typeKey}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{a.plannedStartAt ? formatDate(a.plannedStartAt) : '—'}</td>
                        <td className="py-2"><StatusChip v={a.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </TabsContent>

        {/* ---- Attendance (direct team only, selected period) ---- */}
        <TabsContent value="attendance" className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Days recorded" value={attTotals.days} icon={<CalendarCheck className="w-5 h-5" />} />
            <StatTile label="Hours worked" value={formatMinutes(attTotals.net)} icon={<Clock className="w-5 h-5" />} />
            <StatTile label="Late minutes" value={formatMinutes(attTotals.late)} icon={<CalendarOff className="w-5 h-5" />} />
            <StatTile label="Potential overtime" value={formatMinutes(attTotals.ot)} icon={<BarChart3 className="w-5 h-5" />} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard className="lg:col-span-2" title={`Hours worked (last ${days} days)`} icon={<Clock className="w-5 h-5" />}>
              {attChart.length === 0 ? <Empty text="No attendance recorded for your team in this period — days appear here once scanner imports or self check-ins are reviewed." /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={attChart} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,128,152,0.18)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid rgba(120,128,152,0.2)', fontSize: 12 }} formatter={(v: any) => [`${v}h`]} />
                    <Bar dataKey="hours" name="Hours" fill={ORANGE} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard title="Punctuality">
              {attByMember.filter((r) => r.late > 0).length === 0 ? <Empty text="No late arrivals in this period." /> : (
                <div className="space-y-3">
                  {attByMember.filter((r) => r.late > 0).sort((a, b) => b.late - a.late).slice(0, 8).map((r) => (
                    <div key={r.emp.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{fullName(r.emp)}</span>
                      <span className="text-amber-600 dark:text-amber-400">{formatMinutes(r.late)} late</span>
                    </div>
                  ))}
                </div>
              )}
            </ChartCard>
          </div>
          <ChartCard title="Per member" icon={<Users className="w-5 h-5" />}>
            {team.length === 0 ? <Empty text="No team members report to you yet." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4 font-medium">Member</th>
                      <th className="py-2 pr-4 font-medium">Days</th>
                      <th className="py-2 pr-4 font-medium">Hours</th>
                      <th className="py-2 pr-4 font-medium">Late</th>
                      <th className="py-2 font-medium">Potential OT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attByMember.map((r) => (
                      <tr key={r.emp.id} className="border-b border-border">
                        <td className="py-2 pr-4 text-foreground">{fullName(r.emp)}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{r.days}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{formatMinutes(r.net)}</td>
                        <td className={`py-2 pr-4 ${r.late ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>{r.late ? formatMinutes(r.late) : '—'}</td>
                        <td className="py-2 text-muted-foreground">{r.ot ? formatMinutes(r.ot) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </TabsContent>

        {/* ---- Work Management (feature-gated) ---- */}
        {showWork && (
          <TabsContent value="work" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ChartCard className="lg:col-span-2" title="Work items by status" icon={<ClipboardList className="w-5 h-5" />}>
                {workByStatus.length === 0 ? <Empty text="No work items yet." /> : (
                  <>
                  <p className="text-[11px] text-muted-foreground mb-1">Select a bar to list those items.</p>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={workByStatus} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,128,152,0.18)" vertical={false} />
                      <XAxis dataKey="status" tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: '#7A8099' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid rgba(120,128,152,0.2)', fontSize: 12 }} />
                      <Bar dataKey="count" name="Items" fill={ORANGE} radius={[6, 6, 0, 0]}
                        className="cursor-pointer"
                        onClick={(d: any) => setWorkStatusPick((prev) =>
                          // Clicking the same bar again clears it, so the
                          // drill-down is never a trap you cannot back out of.
                          prev === d?.key ? null : (d?.key ?? null))} />
                    </BarChart>
                  </ResponsiveContainer>
                  </>
                )}
              </ChartCard>
              <ChartCard title="Delivery health">
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Open items</span><span className="text-foreground font-semibold">{openWork.length}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Overdue</span><span className={`font-semibold ${overdueWork.length ? 'text-danger' : 'text-foreground'}`}>{overdueWork.length}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Completed (all time listed)</span><span className="text-foreground font-semibold">{workItems.filter((w) => w.status === 'completed').length}</span></div>
                </div>
              </ChartCard>
            </div>
            {workTruncated && (
              <p className="text-xs text-warning">
                This is the first {WORK_PAGE} work items we could load — there may
                be more. Open Work to see everything.
              </p>
            )}
            {/* Drill-down for the selected bar. The numbers were already
                there; what was missing was the answer to "which ones?" —
                completed, in progress and submitted for completion all read as
                dead ends without it. */}
            {workStatusPick && (() => {
              const picked = rowsForBucket(workItems, workStatusPick);
              return (
                <ChartCard
                  title={`${statusLabel(workStatusPick)} · ${picked.length} item${picked.length === 1 ? '' : 's'}`}
                  icon={<ClipboardList className="w-5 h-5" />}
                  action={(
                    <button type="button" onClick={() => setWorkStatusPick(null)}
                      className="text-xs text-muted-foreground hover:text-foreground underline">
                      Clear
                    </button>
                  )}
                >
                  {picked.length === 0 ? <Empty text="No items with that status." /> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b border-border">
                            <th className="py-2 pr-4 font-medium">Item</th>
                            <th className="py-2 pr-4 font-medium">Owner</th>
                            <th className="py-2 pr-4 font-medium">Due</th>
                            <th className="py-2 pr-4 font-medium">Progress</th>
                            <th className="py-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {picked.slice(0, 50).map((w) => {
                            const overdue = !!w.planned_end_at && w.status !== 'completed'
                              && new Date(w.planned_end_at) < new Date();
                            return (
                              <tr key={w.id} className="border-b border-border hover:bg-muted/40">
                                {/* ⚠️ THE ROW IS A WAY IN, NOT A LABEL. A
                                    drill-down that names the work and then
                                    makes you go and find it is only half an
                                    answer. */}
                                <td className="py-2 pr-4">
                                  <Link
                                    to={workPath(w.id)}
                                    className="text-foreground hover:text-primary-700 underline-offset-2 hover:underline"
                                  >
                                    {w.title}
                                  </Link>
                                </td>
                                <td className="py-2 pr-4 text-muted-foreground">{fullName(w.owner_employee_id ? byEmpId[w.owner_employee_id] : undefined)}</td>
                                {/* ⚠️ text-danger, not text-red-500. The palette
                                    ramp is not the KaziPeople one and has no dark
                                    variant, so a literal red is wrong in one theme;
                                    a ratchet test fails the build when raw palette
                                    classes RISE, and this one tipped it 2560→2561. */}
                                <td className={`py-2 pr-4 ${overdue ? 'text-danger' : 'text-muted-foreground'}`}>
                                  {w.planned_end_at ? formatDate(w.planned_end_at) : '—'}
                                </td>
                                <td className="py-2 pr-4 text-muted-foreground">{Math.round(w.progress || 0)}%</td>
                                <td className="py-2"><StatusChip v={w.status} /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {picked.length > 50 && (
                        <p className="text-[11px] text-muted-foreground mt-2">
                          Showing the first 50 of {picked.length}.
                        </p>
                      )}
                    </div>
                  )}
                </ChartCard>
              );
            })()}

            <ChartCard title="Needs attention" icon={<ClipboardList className="w-5 h-5" />}>
              {overdueWork.length === 0 ? <Empty text="Nothing overdue — nice." /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="py-2 pr-4 font-medium">Item</th>
                        <th className="py-2 pr-4 font-medium">Owner</th>
                        <th className="py-2 pr-4 font-medium">Due</th>
                        <th className="py-2 pr-4 font-medium">Progress</th>
                        <th className="py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overdueWork.slice(0, 12).map((w) => (
                        <tr key={w.id} className="border-b border-border">
                          <td className="py-2 pr-4 text-foreground">{w.title}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{fullName(w.owner_employee_id ? byEmpId[w.owner_employee_id] : undefined)}</td>
                          <td className="py-2 pr-4 text-red-500">{w.planned_end_at ? formatDate(w.planned_end_at) : '—'}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{Math.round(w.progress || 0)}%</td>
                          <td className="py-2"><StatusChip v={w.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ChartCard>
          </TabsContent>
        )}

        {/* ---- Learning (feature-gated; server narrows to the team) ---- */}
        {showLearning && (
          <TabsContent value="learning" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile label="Completion rate" value={completionPct === null ? '—' : `${completionPct}%`} icon={<GraduationCap className="w-5 h-5" />} />
              <StatTile label="Open training" value={learnOpen.length} icon={<ClipboardList className="w-5 h-5" />} />
              <StatTile label="Overdue" value={learnOverdue.length} icon={<Clock className="w-5 h-5" />} />
              <StatTile label="Certs expiring ≤60d" value={expiringCerts.length} icon={<Award className="w-5 h-5" />} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Overdue training" icon={<GraduationCap className="w-5 h-5" />}>
                {learnOverdue.length === 0 ? <Empty text="Nothing overdue." /> : (
                  <div className="space-y-2">
                    {learnOverdue.slice(0, 10).map((a) => (
                      <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2.5 text-sm">
                        <div className="min-w-0">
                          <span className="font-medium text-foreground">{a.course_title}</span>
                          <span className="text-xs text-muted-foreground"> · {fullName(byUserId[String(a.user_id)])}</span>
                        </div>
                        <span className="text-xs text-red-500">due {a.due_date ? formatDate(a.due_date) : '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </ChartCard>
              <ChartCard title="Certifications expiring soon" icon={<Award className="w-5 h-5" />}>
                {expiringCerts.length === 0 ? <Empty text="No certifications expiring in the next 60 days." /> : (
                  <div className="space-y-2">
                    {expiringCerts.slice(0, 10).map((c) => (
                      <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2.5 text-sm">
                        <div className="min-w-0">
                          <span className="font-medium text-foreground">{c.name}</span>
                          <span className="text-xs text-muted-foreground"> · {fullName(byUserId[String(c.user_id)])}</span>
                        </div>
                        <span className="text-xs text-amber-600 dark:text-amber-400">expires {c.expires_on ? formatDate(c.expires_on) : '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </ChartCard>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ManagerReportsPage;
